/**
 * V2 "Put Yoink on your phone" — /phone (default-app wave 2, step 7).
 *
 * The signpost for what the platform already allows: on Android the site
 * has passed Chrome's installability bar since 2026-07-16 (install =
 * full-screen app + "Yoink" in the share sheet via the manifest's
 * share_target); on iPhone/iPad it's Add-to-Home-Screen plus two Apple
 * Shortcuts that put Yoink in the share sheet ("Save to Yoink" → the
 * /save page's ?url= flow) and one tap from logging ("Yoink here").
 *
 * The page shows the section for the device it's opened on; a desktop
 * visitor gets "open this on your phone" with both sets collapsed.
 * Shortcut iCloud links are constants below — they can only be minted
 * from a real Apple device, so until the founder pastes them in, the
 * page shows the build-it-yourself recipe instead (2 minutes, once).
 */
import { showToast } from '../../ui/toast.js';
import {
  isAndroid, isIOS, isStandalone, canPromptInstall, promptInstall, markAppInstalled,
} from '../../core/device.js';

// Paste the "Copy iCloud Link" URLs here once the shortcuts exist
// (Shortcuts app → build per the recipe below → Share → Copy iCloud Link).
export const SHORTCUT_SAVE_URL = '';
export const SHORTCUT_HERE_URL = '';

const SITE = 'yoinkadventures.com/adventure';

function androidSectionHtml() {
  return `
    <section class="phone-section" aria-label="Add the app to your phone">
      <h3>Add the app to your phone</h3>
      <p>One install and Yoink opens full-screen from its own icon — and
        <strong>“Yoink” appears in your share sheet</strong>: tap Share on any
        place in Google Maps and send it straight to your Want-to-go list.</p>
      ${canPromptInstall()
        ? '<button type="button" class="v2-btn-leather" data-phone-install>Install the Yoink app</button>'
        : `<ol class="import-steps">
            <li>Open <strong>${SITE}</strong> in Chrome.</li>
            <li>Tap the <strong>⋮</strong> menu (top right).</li>
            <li>Tap <strong>“Add to Home screen”</strong> (some phones say “Install app”).</li>
          </ol>`}
    </section>`;
}

function iosSectionHtml(deviceWord) {
  const shortcuts = SHORTCUT_SAVE_URL
    ? `<p>Then add the shortcuts — one tap each:</p>
       <p class="phone-shortcut-links">
         <a class="v2-btn-leather" href="${SHORTCUT_SAVE_URL}" target="_blank" rel="noopener">Get “Save to Yoink”</a>
         ${SHORTCUT_HERE_URL ? `<a class="v2-btn-leather" href="${SHORTCUT_HERE_URL}" target="_blank" rel="noopener">Get “Yoink here”</a>` : ''}
       </p>
       <p class="log-hint">“Save to Yoink” joins your share sheet: Share any place
         in Google Maps → Save to Yoink. “Yoink here” logs where you’re standing.</p>`
    : `<details class="import-why">
        <summary>Put Yoink in your share sheet (2 minutes, once)</summary>
        <p>Apple only lets real App Store apps into the share sheet — but the
          built-in <strong>Shortcuts</strong> app can stand in for us:</p>
        <ol class="import-steps">
          <li>Open <strong>Shortcuts</strong> → tap <strong>+</strong> to make a new shortcut.</li>
          <li>Name it <strong>Save to Yoink</strong>. Tap the info button (ⓘ) and turn on
            <strong>“Show in Share Sheet”</strong>.</li>
          <li>Add the action <strong>“URL Encode”</strong> — set its input to
            <strong>Shortcut Input</strong>.</li>
          <li>Add the action <strong>“Open URLs”</strong> and type
            <strong>https://${SITE}/save?url=</strong> then tap the
            <strong>URL Encoded Text</strong> variable to append it.</li>
          <li>Done! Now in Google Maps: <strong>Share → Save to Yoink</strong>.</li>
        </ol>
        <p>Bonus shortcut: one named <strong>Yoink here</strong> with a single
          “Open URLs” action pointed at <strong>https://${SITE}/log</strong> —
          one tap and you’re logging where you stand.</p>
      </details>`;
  return `
    <section class="phone-section" aria-label="Add Yoink to your ${deviceWord}">
      <h3>Add Yoink to your ${deviceWord}</h3>
      <p>Yoink opens full-screen from its own icon, like any app:</p>
      <ol class="import-steps">
        <li>Open <strong>${SITE}</strong> in Safari.</li>
        <li>Tap the <strong>Share</strong> button (the square with the arrow).</li>
        <li>Scroll down and tap <strong>“Add to Home Screen”</strong>.</li>
      </ol>
      ${shortcuts}
    </section>`;
}

export function mount(el, ctx) {
  const { router } = ctx;
  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';

  function render() {
    const android = isAndroid();
    const ios = !android && isIOS();
    const deviceWord = /ipad|macintosh/i.test(navigator.userAgent) ? 'iPad' : 'iPhone';
    const body = android
      ? androidSectionHtml()
      : ios
        ? iosSectionHtml(deviceWord)
        : `<p>This page sets up phones and tablets — open
            <strong>${SITE}/phone</strong> on yours and everything below
            becomes one-tap.</p>
          <details class="import-why"><summary>On Android</summary>${androidSectionHtml()}</details>
          <details class="import-why"><summary>On iPhone or iPad</summary>${iosSectionHtml('iPhone or iPad')}</details>`;
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back">
        <a href="${basePath}/" data-route="discover">← Discover</a></nav>
      <section class="v2-import" aria-label="Put Yoink on your phone">
        <h2 class="v2-page-title">Put Yoink on your phone</h2>
        ${isStandalone()
          ? '<p>You’re already using the app — nothing to set up here. 🎉</p>'
          : body}
      </section>`;
  }

  async function onClick(event) {
    if (event.target.closest('[data-phone-install]')) {
      const accepted = await promptInstall();
      if (accepted) {
        markAppInstalled();
        showToast('Installed! Look for the Yoink icon on your home screen.', { type: 'celebrate' });
      }
      render();
      return;
    }
    const backLink = event.target.closest('a[data-route]');
    if (backLink) {
      event.preventDefault();
      router.navigate(backLink.dataset.route);
    }
  }

  el.addEventListener('click', onClick);
  render();
  return () => el.removeEventListener('click', onClick);
}
